import { Question, GeminiEvaluationResult } from '../types';
import { getConfig } from '../config';
import { getStructuredEvaluationKeyPoints } from './safetyKeyPoints';

interface EvaluationResult {
  isCorrect: boolean;
  score: number;
  feedback: string;
  geminiScore?: number;
  geminiConfidence?: number;
  detailedFeedback?: {
    correctPoints: string[];
    incorrectPoints: string[];
    missedPoints: string[];
  };
}

// Generate comprehensive feedback showing all safety points with checkmarks and X marks
const createComprehensiveFeedback = (
  isCorrect: boolean,
  correctPoints: string[],
  missedPoints: string[],
  structuredKeyPoints: any[]
): string => {
  const getPointDescription = (pointName: string): string => {
    const keyPoint = structuredKeyPoints.find(kp => kp.keyPoint === pointName);
    return keyPoint?.description || '';
  };

  let feedback = '';
  if (isCorrect) {
    feedback = `Good answer! You demonstrated understanding of key safety procedures.\n\n`;
  } else {
    feedback = `Your answer shows some safety awareness but needs more comprehensive coverage.\n\n`;
  }

  // Show complete safety checklist with all points
  feedback += `📋 Complete Safety Checklist:\n\n`;

  // Get all available safety key points
  const allKeyPointCategories = structuredKeyPoints.map(kp => kp.keyPoint);

  // Show all key points with status indicators
  allKeyPointCategories.forEach(point => {
    const description = getPointDescription(point);
    const isMatched = correctPoints.includes(point);
    const statusIcon = isMatched ? '✅' : '❌';
    feedback += `${statusIcon} ${point}: ${description}\n\n`;
  });

  // Add summary and encouragement
  if (isCorrect) {
    feedback += `🎉 Excellent work! You covered ${correctPoints.length} out of ${allKeyPointCategories.length} key safety areas. Keep up the good safety awareness!`;
  } else {
    feedback += `📚 Keep learning! You covered ${correctPoints.length} out of ${allKeyPointCategories.length} key safety areas. You need at least 3 categories for a complete answer. Review the missed points and try again.`;
  }

  return feedback;
};

export const evaluateAnswer = async (
  question: Question,
  selectedOption?: number | null,
  freeResponse?: string
): Promise<EvaluationResult> => {
  if (question.type === 'multiple-choice') {
    return evaluateMultipleChoice(question, selectedOption);
  } else if (question.type === 'free-response') {
    return await evaluateFreeResponse(question, freeResponse || '');
  }

  return {
    isCorrect: false,
    score: 0,
    feedback: 'Invalid question type',
  };
};

const evaluateMultipleChoice = (
  question: Question,
  selectedOption?: number | null
): EvaluationResult => {
  if (selectedOption === null || selectedOption === undefined) {
    return {
      isCorrect: false,
      score: 0,
      feedback: 'Please select an answer.',
    };
  }

  const isCorrect = selectedOption === question.correct;
  const feedback = isCorrect
    ? question.explanation || 'Correct!'
    : question.explanation || `Incorrect. The correct answer is: ${question.options?.[question.correct || 0]}`;

  return {
    isCorrect,
    score: isCorrect ? 1 : 0,
    feedback,
  };
};

const evaluateFreeResponse = async (
  question: Question,
  response: string
): Promise<EvaluationResult> => {
  const config = getConfig();

  // Basic validation
  if (!response.trim()) {
    return {
      isCorrect: false,
      score: 0,
      feedback: 'Please provide an answer.',
    };
  }

  // Try Gemini evaluation if API key is available
  if (config.geminiApiKey) {
    try {
      const geminiResult = await evaluateWithGemini(question, response, config.geminiApiKey);

      // Calculate missed points independently from Gemini response
      const structuredKeyPoints = getStructuredEvaluationKeyPoints();
      const allCategories = structuredKeyPoints.map(kp => kp.keyPoint);
      const correctCategories = geminiResult.correctPoints || [];
      const missedCategories = allCategories.filter(category =>
        !correctCategories.includes(category)
      );

      console.log('🔍 Evaluation Debug:');
      console.log('  Total categories available:', allCategories.length);
      console.log('  Correct categories from Gemini:', correctCategories);
      console.log('  Calculated missed categories:', missedCategories.length);
      console.log('  Gemini missedPoints (ignored):', geminiResult.missedPoints || []);

      // Generate comprehensive feedback with all safety points
      const comprehensiveFeedback = createComprehensiveFeedback(
        geminiResult.isCorrect,
        geminiResult.correctPoints || [],
        missedCategories,
        structuredKeyPoints
      );

      return {
        isCorrect: geminiResult.isCorrect,
        score: geminiResult.score,
        feedback: comprehensiveFeedback,
        geminiScore: geminiResult.score,
        geminiConfidence: geminiResult.confidence,
        detailedFeedback: {
          correctPoints: geminiResult.correctPoints || [],
          incorrectPoints: geminiResult.incorrectPoints || [],
          missedPoints: missedCategories, // Use our calculated missed points, not Gemini's
        },
      };
    } catch (error) {
      console.error('Gemini evaluation failed:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        question: question.question,
        response: response.substring(0, 100) + '...'
      });
      // Fall back to basic evaluation
    }
  }

  // Fallback: Basic keyword matching
  console.log('🔄 Using keyword-based fallback evaluation');
  return evaluateBasicKeywords(question, response);
};

const evaluateWithGemini = async (
  question: Question,
  response: string,
  apiKey: string
): Promise<GeminiEvaluationResult> => {
  console.log('Starting Gemini evaluation for question:', question.question);
  console.log('Response length:', response.length);

  const { GoogleGenerativeAI } = await import('@google/generative-ai');
  const genAI = new GoogleGenerativeAI(apiKey);
  const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });

  // Get structured key points for better evaluation
  const structuredKeyPoints = getStructuredEvaluationKeyPoints();
  const totalKeyPoints = structuredKeyPoints.length;
  const requiredPoints = question.minPoints || 3;

  const prompt = `
You are evaluating a safety quiz answer. Please evaluate the following response:

Question: ${question.question}

Expected key points (student should mention ${requiredPoints} of these ${totalKeyPoints} main safety concepts):

${structuredKeyPoints.map((kp, index) => `${index + 1}. ${kp.keyPoint}
   Acceptable variations: ${kp.variations.join(', ')}`).join('\n\n')}

${question.unsafeAnswers ? `
UNSAFE/INCORRECT concepts that cause IMMEDIATE FAILURE:
${question.unsafeAnswers.map(point => `- ${point}`).join('\n')}
` : ''}

${question.completeAnswer ? `
Complete correct answer for reference:
${question.completeAnswer}
` : ''}

Student's answer: "${response}"

Please provide a JSON response with the following format:
{
  "score": 0.0-1.0,
  "confidence": 0.0-1.0,
  "isCorrect": boolean,
  "feedback": "detailed feedback string with bullet points showing covered and missed points (refer to the student as 'your response' or 'you' rather than 'the student')",
  "correctPoints": ["array", "of", "specific", "phrases", "you", "got", "right"],
  "incorrectPoints": ["array", "of", "specific", "unsafe", "phrases", "you", "mentioned"],
  "missedPoints": ["array", "of", "important", "points", "you", "should", "have", "mentioned"],
  "hasUnsafeContent": boolean,
  "providedCount": number_of_safety_procedures_student_actually_provided
}

Evaluation criteria:
- CRITICAL: Count key point CATEGORIES, not individual variations! Multiple variations of the same category count as ONE key point.
- Example: "tie hair back" AND "tuck hair under cap" both relate to "PPE & Attire: Hair Security" = 1 key point, not 2
- BE EXTREMELY LENIENT: Accept any reasonable variation that matches a key point category
- Accept common variations: "no jewellery"/"no jewelry" = "PPE & Attire: Remove Accessories"
- Accept broad terms: "clean workplace" can count as workspace safety
- Accept similar concepts: "secure material" = "Workpiece Preparation: Secure Material"
- If student mentions ANY unsafe/dangerous concepts: isCorrect = false, hasUnsafeContent = true, score = 0.0
- IMPORTANT: Only 3+ key point categories needed for success (be generous!)
- Score 1.0 if answer covers 3+ distinct key point categories with no unsafe content
- Score 0.6-0.8 if answer covers 2 distinct key point categories with no unsafe content
- Score 0.0-0.5 only if answer has unsafe content or covers fewer than 2 key point categories
- isCorrect should be true if student covers 3+ distinct key point categories AND hasUnsafeContent = false
- correctPoints: the specific KEY POINT CATEGORIES they covered (e.g., "PPE & Attire: Hair Security")
- incorrectPoints: only truly dangerous or unsafe phrases that could cause injury
- missedPoints: Leave this empty [] - the system will calculate missed points automatically
- providedCount: number of distinct KEY POINT CATEGORIES covered (not individual variations)
- DO NOT mention the count in your feedback - just provide direct evaluation of their safety knowledge
- BE GENEROUS - recognize any reasonable safety procedure, don't be picky about exact wording
- If in doubt, give the student the benefit of the doubt and mark as correct
- Address the student directly using "you" and "your response" throughout the feedback

Respond with ONLY the JSON object, no other text.
`;

  const result = await model.generateContent(prompt);
  const responseText = result.response.text();

  // Clean up the response text - remove markdown code blocks if present
  let cleanedResponse = responseText.trim();
  if (cleanedResponse.startsWith('```json')) {
    cleanedResponse = cleanedResponse.replace(/^```json\s*/, '').replace(/\s*```$/, '');
  } else if (cleanedResponse.startsWith('```')) {
    cleanedResponse = cleanedResponse.replace(/^```\s*/, '').replace(/\s*```$/, '');
  }

  try {
    const parsed = JSON.parse(cleanedResponse);
    return {
      score: Math.max(0, Math.min(1, parsed.score || 0)),
      confidence: Math.max(0, Math.min(1, parsed.confidence || 0.5)),
      isCorrect: parsed.isCorrect === true && !parsed.hasUnsafeContent,
      feedback: parsed.feedback || 'Answer evaluated by AI.',
      correctPoints: parsed.correctPoints || [],
      incorrectPoints: parsed.incorrectPoints || [],
      missedPoints: parsed.missedPoints || [],
      hasUnsafeContent: parsed.hasUnsafeContent || false,
      providedCount: parsed.providedCount || 0,
    };
  } catch (parseError) {
    console.error('Failed to parse Gemini response:', responseText);
    console.error('Parse error details:', {
      error: parseError instanceof Error ? parseError.message : 'Unknown parse error',
      responseLength: responseText.length,
      responsePreview: responseText.substring(0, 200),
      question: question.question
    });
    throw new Error('Invalid response format from Gemini');
  }
};

const evaluateBasicKeywords = (
  question: Question,
  response: string
): EvaluationResult => {
  if (!question.keyPoints || question.keyPoints.length === 0) {
    return {
      isCorrect: true,
      score: 1,
      feedback: 'Answer received. Manual review may be required for full evaluation.',
    };
  }

  // Use structured key points for evaluation
  const structuredKeyPoints = getStructuredEvaluationKeyPoints();
  const normalizedResponse = response.toLowerCase();
  const requiredPoints = question.minPoints || 3;

  // Check for unsafe content first
  let hasUnsafeContent = false;
  let unsafeMatches: string[] = [];
  if (question.unsafeAnswers) {
    unsafeMatches = question.unsafeAnswers.filter(unsafe =>
      normalizedResponse.includes(unsafe.toLowerCase())
    );
    hasUnsafeContent = unsafeMatches.length > 0;
  }

  // Check which key point categories are covered with enhanced synonym matching
  const synonymMap: { [key: string]: string[] } = {
    'jewelry': ['jewellery', 'accessories', 'rings', 'watches', 'bracelets', 'necklaces'],
    'jewellery': ['jewelry', 'accessories', 'rings', 'watches', 'bracelets', 'necklaces'],
    'remove': ['no', 'take off', 'avoid wearing'],
    'secure': ['clamp', 'fasten', 'hold', 'attach', 'ensure secure'],
    'material': ['workpiece', 'work', 'stock', 'piece'],
    'workplace': ['work area', 'workspace', 'work space']
  };

  const matchedKeyPoints = structuredKeyPoints.filter(kp => {
    return kp.variations.some(variation => {
      const keywords = variation.toLowerCase().split(' ').filter(word => word.length > 2);

      const matchedWords = keywords.filter(keyword => {
        // Direct match
        if (normalizedResponse.includes(keyword)) return true;

        // Synonym match
        if (synonymMap[keyword]) {
          return synonymMap[keyword].some(synonym => normalizedResponse.includes(synonym));
        }

        // Special case for "no jewelry/jewellery"
        if (keyword === 'remove' && (normalizedResponse.includes('no jewelry') || normalizedResponse.includes('no jewellery'))) {
          return true;
        }

        return false;
      });

      return matchedWords.length >= Math.ceil(keywords.length * 0.5); // Lower threshold to 50%
    });
  });

  const matchedCategories = [...new Set(matchedKeyPoints.map(kp => kp.keyPoint))];
  const matchedPoints = matchedCategories; // For compatibility with existing code

  console.log('🔍 Keyword Evaluation Debug:');
  console.log('  Matched categories:', matchedCategories.length, matchedCategories);
  console.log('  Required points:', requiredPoints);

  const allKeyPointCategories = structuredKeyPoints.map(kp => kp.keyPoint);
  const missedCategories = allKeyPointCategories.filter(category =>
    !matchedCategories.includes(category)
  );
  const missedPoints = missedCategories; // For compatibility with existing code

  // If unsafe content is found, mark as failed
  if (hasUnsafeContent) {
    return {
      isCorrect: false,
      score: 0,
      feedback: `UNSAFE CONTENT DETECTED: Your answer contains unsafe practices that could lead to injury. This is a safety quiz and any unsafe recommendations result in failure. Please review the safety guidelines and try again.`,
      detailedFeedback: {
        correctPoints: matchedPoints,
        incorrectPoints: unsafeMatches,
        missedPoints: allKeyPointCategories, // Show all points as missed since they failed
      },
    };
  }

  const totalKeyPoints = structuredKeyPoints.length;
  const score = matchedPoints.length / totalKeyPoints;
  const isCorrect = matchedPoints.length >= requiredPoints;

  // Use the comprehensive feedback function for consistency
  const feedback = createComprehensiveFeedback(
    isCorrect,
    matchedPoints,
    missedPoints,
    structuredKeyPoints
  );

  return {
    isCorrect,
    score,
    feedback,
    detailedFeedback: {
      correctPoints: matchedPoints,
      incorrectPoints: [], // Basic evaluation can't detect nuanced incorrect points
      missedPoints,
    },
  };
};